/**
 * Live household screens (#580, live updates PR 1): one connection per tab, and each mounted screen
 * RE-READS when the household changes.
 *
 * **An event is never data.** Every listener below only marks screens stale. The screen then asks
 * the same REST read it always asked, and shows what that says. No payload value — a user id, an
 * amount, an approval id — reaches what a screen shows, so a duplicated, reordered or forged event can
 * cost one extra read and can never change a balance. Bulk `approval.granted` carries no `userId` and a
 * settlement need not name anybody, which is why the five screens filter nothing by payload either: the
 * server decides what each reader may see. (Trophies' one exception is below.)
 *
 * **Missed signals are recovered by reading again, not by replay.** Seven of these events are sent
 * at most once. A screen therefore also re-reads when the socket (re)joins, when the tab becomes
 * visible again, and every 30 seconds (plus a little jitter) while the tab is visible. That interval
 * is a recovery bound, not a latency promise, and it is the ONLY path for changes that send no event
 * at all. It is not instant and must not be described as instant. A hidden tab does not poll; it
 * re-reads when it is shown.
 *
 * **Payday holds (#580 unit 11a, d-20260928-52).** Wait, Talk it over and We talked it over send
 * `payday.hold.changed` to the household's CURRENT parents only, through a parent room the server
 * re-checks on every send; a child's or a display's socket never receives it. It carries `{}`, so it
 * only marks the two screens that show holds (Approvals and the Dashboard, both through
 * `GET /api/payday/preview`) stale. A missed one is recovered by the same 30-second re-read.
 *
 * **Who this connects for.** A signed-in family member only: never a shared display (the kiosk keeps
 * its own device connection in `DisplayRoute.jsx`, and a device session is never a person here),
 * never a platform operator, never nobody. Person and device rooms stay disjoint on the server.
 *
 * **Trophies is the one hinted screen** (#583 unit 10). It keeps its own recovery read (design D5: it re-reads
 * on focus and never polls), so it is not one of the five. `useHouseholdHint` tells it when a badge was
 * earned or the socket (re)joined, and it starts that same read. The hint carries the payload's member id
 * only so Trophies can skip a badge plainly earned by somebody else; it never shows or trusts anything else.
 *
 * Which screen re-reads on which event is `SCREENS_BY_EVENT` below. The listeners are written out
 * one per line, because `app/test/event-compatibility.test.js` finds them by their literal text.
 */
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { ApiError } from '../api/client.js';
import { createBoardSocket } from '../api/boardSocket.js';
import { useWebSocket } from '../hooks/useWebSocket.js';
import { createReviewMemory } from '../lib/approvalEcho.js';
import { useOptionalActor } from './ActorContext.jsx';

/** The five screens whose reads this invalidates: Today, Dashboard, Wallet, Approvals, Village. */
export const SCREENS = Object.freeze(['today', 'dashboard', 'wallet', 'approvals', 'village']);
const ALL = SCREENS;

/** Screens told only of their own events and of a (re)join: never the interval, visibility or online. */
export const HINTED_SCREENS = Object.freeze(['trophies']);

/**
 * Design §2's matrix. An event names the screens whose DATA it can change; a screen it does not
 * name is not re-read. `display.paired` changes nothing any of them shows, so it is not listened for.
 */
export const SCREENS_BY_EVENT = Object.freeze({
  choreCompleted: ALL,
  choreUncompleted: ALL,
  choreReassigned: Object.freeze(['today', 'dashboard', 'approvals', 'village']),
  choreReordered: Object.freeze(['today', 'dashboard']),
  'family:reset': ALL,
  'approval.requested': ALL,
  'approval.granted': ALL,
  // #580 unit 11: a parent's HOLD. An empty re-read signal; Today's echo reads its own reviews.
  'approval.reviewed': ALL,
  'payday.settled': ALL,
  'payday.hold.changed': Object.freeze(['dashboard', 'approvals']),
  'badge.earned': HINTED_SCREENS,
});

export const RECONCILE_MS = 30_000;
export const RECONCILE_JITTER_MS = 2_000;
export const COALESCE_MS = 150;
export const COALESCE_MAX_MS = 500;
export const RETRY_STEPS_MS = Object.freeze([1_000, 2_000, 5_000, 10_000, 30_000]);
/** A background read that has not answered by now counts as failed, so it cannot block the next. */
export const READ_TIMEOUT_MS = 20_000;

/** What a screen says when it is not live, by `useLiveRefresh` status. `off` and `live` say nothing here. */
export const LIVE_LABELS = Object.freeze({
  updating: 'Updating…',
  paused: 'Updates paused',
  polling: 'Updates every 30 seconds',
});

/**
 * The opening words of a live screen's note. "Live family data." is a claim about freshness, so it
 * is said only while the status is `live`. Otherwise the note opens with what IS true — the
 * label above — or with nothing at all when the screen has no household connection (`off`).
 */
export function freshnessLead(status) {
  if (status === 'live') return 'Live family data.';
  const label = LIVE_LABELS[status];
  if (!label) return '';
  return label.endsWith('…') ? label : `${label}.`;
}

const Hub = createContext(null);
const Link = createContext(null);
const Reviews = createContext(null);

const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

/** The authority a household socket may speak for, or `null`. */
function personAuthority(actor) {
  if (!actor || (actor.kind !== 'parent' && actor.kind !== 'child')) return null;
  if (!actor.tenantId || actor.id == null || actor.id === '') return null;
  return `${actor.tenantId}:${actor.kind}:${actor.id}`;
}

let epochs = 0;

/**
 * What starts a new epoch. A NEW actor object is a new sign-in, even for the same person. #1003: except a family
 * turn's child, which carries its turn's generation: each renewal (`ChildActorReturn`) publishes a new actor object
 * for the same child and generation, and that is the same turn, so it keeps the review memory and the re-read
 * timer. A new generation, or another child, is a new turn and a new epoch.
 */
function epochKeyOf(actor) {
  const person = personAuthority(actor);
  if (person && actor.kind === 'child' && typeof actor.generation === 'string' && actor.generation) {
    return `${person}@${actor.generation}`;
  }
  return actor;
}

/**
 * @param {object} props
 * @param {object|null} [props.actor]  overrides the `ActorProvider`'s actor (tests)
 * @param {typeof createBoardSocket} [props.socketFactory]
 */
export function HouseholdRealtimeProvider({ children, actor: actorProp, socketFactory = createBoardSocket }) {
  const actorContext = useOptionalActor();
  const actor = actorProp !== undefined ? actorProp : actorContext?.actor ?? null;
  // A NEW actor object is a new sign-in, even for the same person, so it is a new epoch (`epochKeyOf`).
  const epochKey = epochKeyOf(actor);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const epoch = useMemo(() => (epochs += 1), [epochKey]);
  const person = personAuthority(actor);
  const authority = person ? `${person}#${epoch}` : null;

  const subscribers = useRef(new Set());
  const joins = useRef(0);
  const refreshActor = useRef(null);
  refreshActor.current = actorContext?.refresh ?? null;
  const lastReverify = useRef(-Infinity);

  const hub = useMemo(() => {
    if (!authority) return null;
    const state = { authority, joins: 0 };
    return {
      state,
      subscribe(entry) {
        subscribers.current.add(entry);
        return () => subscribers.current.delete(entry);
      },
      invalidate(screens, cause, payload) {
        for (const entry of [...subscribers.current]) {
          if (screens.includes(entry.screen)) entry.invalidate(cause, payload);
        }
      },
      /**
       * A 401 on a background read: ask the server who is signed in, at most every 30 s. Answers
       * the re-verify's promise (the actor, or `null`), or `null` when none was asked.
       */
      unauthorized() {
        const now = Date.now();
        if (now - lastReverify.current < RECONCILE_MS) return null;
        lastReverify.current = now;
        return refreshActor.current ? Promise.resolve(refreshActor.current()) : null;
      },
    };
  }, [authority]);

  // #580 unit 11: what Today's approval echo has already seen, for THIS sign-in only (lib/approvalEcho.js).
  // A new authority is a new store, so a sign-out or a new sign-in forgets it; leaving Today does not.
  const reviewMemory = useMemo(() => (authority ? createReviewMemory() : null), [authority]);

  const hubRef = useRef(hub);
  hubRef.current = hub;
  const everyone = (cause) => hubRef.current?.invalidate(ALL, cause);

  const link = useWebSocket({
    // #580 D1 (#902): the server refuses a family turn's socket in every turn state, and a refused
    // socket only reconnects. Screens keep their 30-second re-read, which is shown as such.
    authority: actorContext?.familyTurn ? null : authority,
    socketFactory,
    onJoin() {
      joins.current += 1;
      if (hubRef.current) hubRef.current.state.joins = joins.current;
      // Listeners are already installed, so this read cannot miss an event sent after the join.
      hubRef.current?.invalidate([...ALL, ...HINTED_SCREENS], 'join');
    },
    install(socket) {
      const on = (screens) => (payload) => hubRef.current?.invalidate(screens, 'event', payload);
      socket.on('choreCompleted', on(SCREENS_BY_EVENT.choreCompleted));
      socket.on('choreUncompleted', on(SCREENS_BY_EVENT.choreUncompleted));
      socket.on('choreReassigned', on(SCREENS_BY_EVENT.choreReassigned));
      socket.on('choreReordered', on(SCREENS_BY_EVENT.choreReordered));
      socket.on('family:reset', on(SCREENS_BY_EVENT['family:reset']));
      socket.on('approval.requested', on(SCREENS_BY_EVENT['approval.requested']));
      socket.on('approval.granted', on(SCREENS_BY_EVENT['approval.granted']));
      socket.on('approval.reviewed', on(SCREENS_BY_EVENT['approval.reviewed']));
      socket.on('payday.settled', on(SCREENS_BY_EVENT['payday.settled']));
      socket.on('payday.hold.changed', on(SCREENS_BY_EVENT['payday.hold.changed']));
      socket.on('badge.earned', on(SCREENS_BY_EVENT['badge.earned']));
    },
  });

  // Visible-tab reconciliation, and recovery on return, bfcache restore and coming back online.
  const [visible, setVisible] = useState(() => !isHidden());
  useEffect(() => {
    if (!authority) return undefined;
    const show = () => {
      setVisible(!isHidden());
      if (!isHidden()) everyone('visible');
    };
    const online = () => everyone('online');
    document.addEventListener('visibilitychange', show);
    window.addEventListener('pageshow', show);
    window.addEventListener('online', online);
    return () => {
      document.removeEventListener('visibilitychange', show);
      window.removeEventListener('pageshow', show);
      window.removeEventListener('online', online);
    };
  }, [authority]);
  useEffect(() => {
    if (!authority || !visible) return undefined;
    let timer;
    const next = () => {
      timer = setTimeout(() => {
        everyone('interval');
        next();
      }, RECONCILE_MS + Math.floor(Math.random() * RECONCILE_JITTER_MS));
    };
    next();
    return () => clearTimeout(timer);
  }, [authority, visible]);

  const linkValue = useMemo(
    () => (hub ? { joined: link.joined, visible } : null),
    [hub, link.joined, visible],
  );
  return (
    <Hub.Provider value={hub}>
      <Link.Provider value={linkValue}>
        <Reviews.Provider value={reviewMemory}>{children}</Reviews.Provider>
      </Link.Provider>
    </Hub.Provider>
  );
}

/**
 * Keep one mounted screen's read current.
 *
 * `read(signal)` is the screen's own REST read; `apply(data)` publishes it and returns `false` when
 * the answer was unreadable, or `deferred` when the screen deliberately kept its older snapshot.
 * A deferred answer stays updating until a later accepted read. While `enabled` is false (the
 * screen's first read has not landed, or it is showing an error with its own Retry) nothing runs.
 *
 * Per screen: one read at a time. An invalidation during a read queues ONE trailing read, and a
 * burst is coalesced for 150 ms, never longer than 500 ms. A failed read keeps what is on screen,
 * says updates are paused, and retries after 1, 2, 5, 10 then every 30 seconds. A 403 stops
 * retrying, because asking again will not change the answer. A 401 stops READING, and from then on
 * each invalidation asks the server who is signed in instead (at most every 30 s); the screen reads
 * again once a re-verify names somebody. A hidden tab reports `paused`: it is not updating.
 * Unmounting aborts the read and drops its answer. With requireCleanRead, an invalidated answer
 * is also discarded before apply; only the trailing read may publish status or an echo.
 *
 * The answer is `live` only after a read that STARTED after the latest join succeeded with no
 * invalidation since. So a read made before the socket joined can never be called live.
 *
 * @returns {{ status: 'off'|'live'|'updating'|'paused'|'polling', invalidate: () => void }}
 */
export function useLiveRefresh(screen, { read, apply, enabled = true, requireCleanRead = false }) {
  const hub = useContext(Hub);
  const link = useContext(Link);
  const fns = useRef({ read, apply, requireCleanRead });
  fns.current = { read, apply, requireCleanRead };
  const [fresh, setFresh] = useState({ joins: -1, busy: true, failed: false });
  const control = useRef(null);

  useEffect(() => {
    if (!hub || !enabled) return undefined;
    const s = { dirty: false, running: false, timer: null, firstAt: 0, retry: null, attempt: 0, stopped: false, unauthorized: false, gone: false, controller: null };

    // Signed out, as far as the last read knows: no reads until a re-verify names somebody.
    function reverify() {
      const asked = hub.unauthorized();
      asked?.then((next) => {
        if (!next || s.gone || !s.unauthorized) return;
        s.unauthorized = false;
        schedule();
      }, () => {});
    }

    function schedule() {
      if (s.gone) return;
      if (s.unauthorized) { reverify(); return; }
      if (s.stopped) return;
      if (isHidden()) { s.dirty = true; return; }
      if (s.running) { s.dirty = true; return; }
      const now = Date.now();
      if (!s.timer) s.firstAt = now;
      clearTimeout(s.timer);
      s.timer = setTimeout(run, Math.max(0, Math.min(COALESCE_MS, COALESCE_MAX_MS - (now - s.firstAt))));
    }

    function run() {
      s.timer = null;
      clearTimeout(s.retry);
      s.retry = null;
      if (s.gone) return;
      // A retry or a coalesced read that comes due while the tab is hidden waits for it to show.
      if (isHidden()) { s.dirty = true; return; }
      s.dirty = false;
      s.running = true;
      const joinsAtStart = hub.state.joins;
      const controller = new AbortController();
      s.controller = controller;
      setFresh((f) => ({ ...f, busy: true }));
      let timer;
      const late = new Promise((_resolve, reject) => {
        timer = setTimeout(() => { reject(new Error('timed out')); controller.abort(); }, READ_TIMEOUT_MS);
      });
      Promise.race([Promise.resolve().then(() => fns.current.read(controller.signal)), late])
        .then((data) => {
          if (s.gone || controller.signal.aborted) return;
          // An echo must wait for the trailing read when an invalidation overtook this response.
          if (s.dirty && fns.current.requireCleanRead) {
            setFresh({ joins: -1, busy: true, failed: false });
            return;
          }
          const applied = fns.current.apply(data);
          if (applied === false) throw new Error('unreadable');
          if (applied === 'deferred') {
            setFresh({ joins: -1, busy: true, failed: false });
            return;
          }
          s.attempt = 0;
          // An invalidation during this read means it may already be out of date.
          setFresh({ joins: s.dirty ? -1 : joinsAtStart, busy: s.dirty, failed: false });
        })
        .catch((error) => {
          // Only unmounting (`gone`) drops a failure; a read the timeout aborted is still a failure.
          if (s.gone) return;
          const status = error instanceof ApiError ? error.status : null;
          setFresh({ joins: -1, busy: false, failed: true });
          if (status === 401) { s.unauthorized = true; reverify(); return; }
          if (status === 403) { s.stopped = true; return; }
          if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
          const wait = RETRY_STEPS_MS[Math.min(s.attempt, RETRY_STEPS_MS.length - 1)];
          s.attempt += 1;
          s.retry = setTimeout(run, wait);
        })
        .finally(() => {
          clearTimeout(timer);
          s.running = false;
          if (s.dirty && !s.gone) schedule();
        });
    }

    const entry = {
      screen,
      invalidate() {
        setFresh((f) => (f.busy ? f : { ...f, busy: true }));
        schedule();
      },
    };
    control.current = entry;
    const unsubscribe = hub.subscribe(entry);
    // The screen's first read may have gone out before this subscription existed, so an event in
    // that gap would be lost. One read now closes the gap.
    entry.invalidate();
    return () => {
      s.gone = true;
      clearTimeout(s.timer);
      clearTimeout(s.retry);
      s.controller?.abort();
      unsubscribe();
      if (control.current === entry) control.current = null;
    };
  }, [hub, enabled, screen]);

  let status = 'off';
  if (hub && link && enabled) {
    if (fresh.failed || !link.visible) status = 'paused';
    else if (!link.joined) status = 'polling';
    else if (fresh.busy || fresh.joins !== hub.state.joins) status = 'updating';
    else status = 'live';
  }
  const invalidate = useMemo(() => () => control.current?.invalidate(), []);
  return { status, invalidate };
}

/** #580 unit 11: this sign-in's review memory for the approval echo, or `null` with no household connection. */
export function useReviewMemory() {
  return useContext(Reviews);
}

/**
 * Tell a hinted screen (`HINTED_SCREENS`) about its own events and every (re)join, while it is mounted.
 * `onHint(cause, payload)` runs with cause `'event'` or `'join'`; a join has no payload. The screen does
 * its own read: the payload is a hint, never data. Outside a signed-in household nothing is called.
 */
export function useHouseholdHint(screen, onHint, { skipInitialJoin = false } = {}) {
  const hub = useContext(Hub);
  const handler = useRef(onHint);
  handler.current = onHint;
  useEffect(() => {
    if (!hub) return undefined;
    // A route that already reads on arrival can opt out of the first join, but never a rejoin.
    let initialJoin = skipInitialJoin && hub.state.joins === 0;
    return hub.subscribe({ screen, invalidate: (cause, payload) => {
      if (cause === 'join' && initialJoin) { initialJoin = false; return; }
      handler.current?.(cause, payload);
    } });
  }, [hub, screen, skipInitialJoin]);
}
